/** A utility for constructing URL paths for requests sent to the OBX server. */
class ApiRequestPaths {
  /** Returns the endpoint to report a critical client error. */
  static errorReport = "Legacy/V0/ErrorReport";

  /** Returns the endpoint to get the calendar event url for a given appointment.
   * @param leadId - The id of the session's lead.
   * @param customerId - The id of the customer who will be attending the appointment.
   * @param appointmentId - The id of the appointment to obtain the calendar event url for.
   */
  static getCalendarEventUrl = (leadId: string, customerId: string, appointmentId: string) =>
    `Legacy/V0/Contacts/${leadId}/Customers/${customerId}/Appointments/${appointmentId}/CalendarEvent`;

  /** Returns the endpoint path to get the centers available for booking the current session.
   * @param postalCode - The postal code of the origin for the center lookup.
   * @param centerId - The id of the center to which the customer is assigned.
   */
  static getCentersV3 = (postalCode: string, centerId: string) =>
    `Legacy/V3/Centers?postalCode=${postalCode}&centerId=${centerId}`;

  /** Returns the endpoint path to get the experiments that are active for the current session. */
  static getActiveExperiments = `Legacy/V2/Experiments`;

  /** Returns the endpoint path to get the services available for booking at a given center. */
  static getCenterServices = (centerId: string) => `Legacy/V2/Centers/${centerId}/Services`;

  /** Returns the endpoint path to create a new customer note. */
  static postCustomerNote = (leadId: string, customerId: string) =>
    `Legacy/V0/Contacts/${leadId}/Customers/${customerId}/Notes`;

  /** Returns the endpoint path to update an existing customer note. */
  static putCustomerNote = (leadId: string, customerId: string, noteId: string) =>
    `Legacy/V0/Contacts/${leadId}/Customers/${customerId}/Notes/${noteId}`;

  /** Returns the endpoint path to report the session's device information. */
  static postDeviceInfo = (leadId: string) => `Legacy/V0/Contacts/${leadId}/Devices`;

  /** Returns the endpoint path to create the customer experiment assignment.
   * @param leadId - The id of the session's lead.
   * @param customerId - The id of the customer to be assigned to the experiment.
   * @param experimentId - The id of the experiment to assign the customer to.
   */
  static postCustomerExperiment = (leadId: string, customerId: string, experimentId: string) =>
    `Legacy/V2/Contacts/${leadId}/Customers/${customerId}/Experiments/${experimentId}`;

  /** Returns the endpoint path to get an OPS calendar by its center.
   * @param leadId - The id of the session's lead.
   * @param customerId - The id of the customer who will be booking with the calendar.
   * @param centerId - The id of the center to which the retrieved calendar belongs.
   */
  static getOpsCenterCalendar = (leadId: string, customerId: string, centerId: string) =>
    `Legacy/V2/Ops/Contacts/${leadId}/Customers/${customerId}/Centers/${centerId}/Calendars`;

  /** Returns the endpoint path to confirm a reserved appointment in the OPS Booking system.
   * @param leadId - The id of the sessions lead.
   * @param customerId - The id of the customer who will be making the reservation.
   * @param centerId - The id of the center where the reservation will be made.
   * @param slotId - The id of the slot which will be reserved.
   */
  static postOpsAppointmentUrlV1_1 = (leadId: string, customerId: string, centerId: string, slotId: number) =>
    `Legacy/V1_1/Ops/Contacts/${leadId}/Customers/${customerId}/Centers/${centerId}/Appointments/Confirm/${slotId}`;

  /** Returns the endpoint path create a reservation in the OPS Booking system.
   * @param leadId - The id of the sessions lead.
   * @param customerId - The id of the customer who will be making the reservation.
   * @param centerId - The id of the center where the reservation will be made.
   * @param serviceId - The id of the service which will be conducted at the reserved appointment.
   * @param slotId - The id of the slot which will be reserved.
   */
  static postOpsReservationUrlV1_1 = (
    leadId: string,
    customerId: string,
    centerId: string,
    serviceId: string,
    slotId: number
  ) =>
    `Legacy/V1_1/Ops/Contacts/${leadId}/Customers/${customerId}/Centers/${centerId}/Services/${serviceId}/Slots/${slotId}`;

  /** Returns the endpoint path to update the session's customer for customers that are assigned to an OPS supported center.
   * @param leadId - The id of the session's lead.
   * @param customerId - The id of the customer to be updated.
   */
  static putOpsCustomerUrl = (leadId: string, customerId: string) =>
    `Legacy/V0/Ops/Contacts/${leadId}/Customers/${customerId}`;

  /** Returns the endpoint path to update the session's lead result.
   * @param leadId - The id of the session's lead.
   */
  static postLeadResult = (leadId: string) => `Legacy/V0/Contacts/${leadId}/Result`;

  /** The endpoint path used to obtain the data required for the session to start. */
  static postSessionStart = "Legacy/V2/SessionStart";

  /** Returns an endpoint to update the current step progress for the session.
   * @param leadId - The id of the session's lead.
   * @param flowId - The id of the session's flow.
   */
  static postStepProgress = (leadId: string, flowId: string) => `Legacy/V0/Contacts/${leadId}/Flows/${flowId}/steps`;

  /** Returns the endpoint path to be used when updating the auth token. */
  static putToken = `Legacy/V0/Token`;
}

export default ApiRequestPaths;
